/**
 * src/api/probe-engines.ts
 *
 * Typed wrappers for /v1/observe/probe-engines/*. Backs the
 * Scrapers/Diagnose tab (this PR) and the Scrapers/Onboard tab's
 * recon stage (later). Same engine layer powers both.
 */

import { request, getApiKey, getBaseUrl } from './client';

export type EngineKind =
  | 'direct_http'
  | 'docker_http'
  | 'docker_browser'
  | 'cf_http'
  | 'cf_browser';

export interface HeaderPair { name: string; value: string }

export interface BrowserOpts {
  searchSelector?:   string;
  searchKeyword?:    string;
  jsInject?:         string;
  waitForSelector?:  string;
  screenshot?:       boolean;
  humanevasionTier?: 'js' | 'cdp' | 'vm';
  useHumanBehavior?: boolean;
}

export interface ProbeRequest {
  url:        string;
  method?:    'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD';
  headers?:   HeaderPair[];
  body?:      string;
  proxy?:     string;
  timeoutMs?: number;
  engine:     EngineKind;
  browser?:   BrowserOpts;
}

export interface ProbeTiming {
  dns?:     number;
  connect?: number;
  tls?:     number;
  ttfb?:    number;
  total:    number;
}

export type BlockSeverity = 'hard' | 'soft' | 'info';
export type BlockVerdict  = 'ok' | 'soft_block' | 'hard_block';

export interface BlockSignal {
  id:        string;
  severity:  BlockSeverity;
  matchedAt: 'body' | 'headers' | 'status' | 'cookies';
  excerpt?:  string;
}

export interface ProbeResult {
  id:              string;
  ts:              string;
  engine:          EngineKind;
  durationMs:      number;
  request:         ProbeRequest;
  statusCode:      number | null;
  finalUrl:        string;
  responseHeaders: Record<string, string>;
  bodySizeBytes:   number;
  bodyPreview:     string;
  bodyFullId?:     string;
  timing:          ProbeTiming;
  redirectChain:   string[];
  renderedHtmlId?: string;
  screenshotId?:   string;
  consoleLog?:     Array<{ level: string; text: string; ts: number }>;
  networkLog?:     Array<{ url: string; method: string; status: number | null; type: string; size: number; ms: number }>;
  cookies?:        Array<{ name: string; value: string }>;
  jsInjectResult?: unknown;
  blockSignals:    BlockSignal[];
  blockVerdict:    BlockVerdict;
  extractedCount?: number;
  error?: { kind: string; message: string };
}

export interface EngineCapability {
  available:        boolean;
  supportsProxy:    boolean;
  supportsBrowser:  boolean;
  description:      string;
}

export interface ProbeRunSummary {
  id:           string;
  ts:           string;
  engine:       EngineKind;
  durationMs:   number;
  url:          string;
  statusCode:   number | null;
  blockVerdict: BlockVerdict;
  hasError:     boolean;
}

// ── Engines + capability table ─────────────────────────────────────
export function getEngines() {
  return request<{ ok: true; engines: Record<EngineKind, EngineCapability> }>(
    '/v1/observe/probe-engines/engines',
  );
}

// ── Single-engine probe ────────────────────────────────────────────
export function runProbe(req: ProbeRequest) {
  return request<{ ok: true; result: ProbeResult }>(
    '/v1/observe/probe-engines/run',
    { method: 'POST', body: JSON.stringify(req) },
  );
}

// ── Compare mode ───────────────────────────────────────────────────
export interface CompareReq {
  url:        string;
  engines:    EngineKind[];
  method?:    ProbeRequest['method'];
  headers?:   HeaderPair[];
  body?:      string;
  proxy?:     string;
  timeoutMs?: number;
  browser?:   BrowserOpts;
}
export function runCompare(req: CompareReq) {
  return request<{ ok: true; groupId: string; results: ProbeResult[] }>(
    '/v1/observe/probe-engines/compare',
    { method: 'POST', body: JSON.stringify(req) },
  );
}

// ── History ────────────────────────────────────────────────────────
export function listHistory(opts: { limit?: number; engine?: EngineKind; before?: string } = {}) {
  const q = new URLSearchParams();
  if (opts.limit)  q.set('limit',  String(opts.limit));
  if (opts.engine) q.set('engine', opts.engine);
  if (opts.before) q.set('before', opts.before);
  const qs = q.toString();
  return request<{ ok: true; runs: ProbeRunSummary[] }>(
    `/v1/observe/probe-engines/history${qs ? '?' + qs : ''}`,
  );
}

export function getRun(id: string) {
  return request<{ ok: true; run: ProbeResult }>(
    `/v1/observe/probe-engines/history/${encodeURIComponent(id)}`,
  );
}

/**
 * Fetch a GridFS artefact (rendered HTML or PNG) and return an
 * object URL the frontend can put in <iframe srcDoc> or <img src>.
 *
 * Why a fetch+blob bridge instead of just `<img src=/artefact/:id>`?
 * The /artefact route is auth-gated by `x-api-key`. <img> and
 * <iframe> can't send custom headers, so the browser's bare GET
 * would 401. We do an authenticated fetch from JS, get the bytes,
 * make a blob URL, and assign that. Object URLs are scoped to the
 * document and freed by the caller via revokeObjectURL.
 *
 * Returns { url, contentType, revoke }. The revoke fn is the
 * caller's responsibility — usually fired in a useEffect cleanup.
 */
export async function fetchArtefact(id: string): Promise<{
  url:         string;
  contentType: string;
  revoke:      () => void;
}> {
  const apiKey = getApiKey() ?? '';
  const base   = getBaseUrl();
  const resp   = await fetch(`${base}/v1/observe/probe-engines/artefact/${encodeURIComponent(id)}`, {
    headers: { 'x-api-key': apiKey },
  });
  if (!resp.ok) {
    throw new Error(`artefact fetch failed: HTTP ${resp.status}`);
  }
  const blob        = await resp.blob();
  const contentType = resp.headers.get('content-type') ?? blob.type ?? 'application/octet-stream';
  const url         = URL.createObjectURL(blob);
  return { url, contentType, revoke: () => URL.revokeObjectURL(url) };
}
